(() => {
  const slideFor = (image) => image?.closest("[data-swiper-slide]");
  let observer;
  let videoPreloadObserver;
  let videoPlaybackObserver;
  const boundImages = new WeakSet();
  const boundVideos = new WeakSet();

  const markSkeleton = (image, on) => {
    const slide = slideFor(image);
    if (!slide) return;
    slide.classList.toggle("is-media-skeleton", on);
  };

  const isLoaded = (image) =>
    image.complete && image.naturalWidth > 0 && image.naturalHeight > 0;

  const activateImage = (image) => {
    if (!image?.dataset.pdpSrc && !image?.dataset.pdpSrcset) return;

    if (image.dataset.pdpSrcset) {
      image.srcset = image.dataset.pdpSrcset;
      image.removeAttribute("data-pdp-srcset");
    }
    if (image.dataset.pdpSrc) {
      image.src = image.dataset.pdpSrc;
      image.removeAttribute("data-pdp-src");
    }
  };

  const observeImage = (image) => {
    if (!image?.dataset.pdpSrc && !image?.dataset.pdpSrcset) return;
    if (!("IntersectionObserver" in window)) {
      activateImage(image);
      return;
    }

    if (!observer) {
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            observer.unobserve(entry.target);
            activateImage(entry.target);
          });
        },
        // First/visible media is already loaded by the template and the
        // desktop collage warms its immediate next slide explicitly. Keep this
        // observer close to the viewport so distant color/media images do not
        // compete with the PDP's first paint.
        { rootMargin: "350px 35%" },
      );
    }

    observer.observe(image);
  };

  const preloadImage = (image) => {
    if (!(image instanceof HTMLImageElement)) return;
    activateImage(image);
    if (image.loading === "lazy") image.loading = "eager";
    bindImage(image);
  };

  /** Eagerly warm gallery images by slide index range (inclusive). */
  window.preloadPdpGallerySlides = (galleryEl, fromIndex, count = 1) => {
    if (!galleryEl) return;
    const slides = galleryEl.querySelectorAll("[data-swiper-slide]");
    const start = Math.max(0, fromIndex | 0);
    const end = Math.min(slides.length, start + Math.max(1, count | 0));
    for (let i = start; i < end; i++) {
      slides[i]
        ?.querySelectorAll(".wt-product__img")
        .forEach(preloadImage);
    }
  };

  const ensureVideoLoadingMount = (video) => {
    let mount =
      video.closest("[data-pdp-video-slide]") ||
      video.closest("[data-swiper-slide]");
    if (!mount) return null;

    // Prefer the dedicated video wrapper when present.
    const wrapper = video.closest("[data-pdp-video-slide]");
    if (wrapper) mount = wrapper;

    if (!mount.querySelector(".pdp-video-loading")) {
      mount.insertAdjacentHTML(
        "beforeend",
        '<div class="pdp-video-loading" aria-hidden="true"><div class="pdp-video-loading__track"><div class="pdp-video-loading__bar"></div></div></div>',
      );
    }
    if (!mount.hasAttribute("data-pdp-video-slide")) {
      mount.setAttribute("data-pdp-video-slide", "");
    }
    return mount;
  };

  const bindVideoLoadingBar = (video) => {
    if (!(video instanceof HTMLVideoElement)) return;
    if (video.dataset.pdpVideoUiBound === "true") return;

    const slide = ensureVideoLoadingMount(video);
    if (!slide) return;

    video.dataset.pdpVideoUiBound = "true";
    const loadingBar = slide.querySelector(".pdp-video-loading__bar");
    let shownAt = 0;
    let hideTimer = 0;
    const minVisibleMs = 900;

    const galleryIsCovered = () =>
      Boolean(video.closest("gallery-section")?.classList.contains("loading"));

    const getBufferedRatio = () => {
      const { buffered, duration } = video;
      if (!buffered.length || !Number.isFinite(duration) || duration <= 0) {
        return null;
      }

      let end = 0;
      for (let i = 0; i < buffered.length; i += 1) {
        try {
          end = Math.max(end, buffered.end(i));
        } catch (_) {
          /* ignore InvalidStateError */
        }
      }
      return Math.min(end / duration, 1);
    };

    const updateLoadingProgress = () => {
      if (!loadingBar) return;

      const ratio = getBufferedRatio();
      if (ratio === null) {
        slide.classList.add("is-video-loading--indeterminate");
        loadingBar.style.width = "";
        return;
      }

      slide.classList.remove("is-video-loading--indeterminate");
      loadingBar.style.width = `${Math.max(12, Math.round(ratio * 100))}%`;
    };

    const showLoading = () => {
      if (hideTimer) {
        window.clearTimeout(hideTimer);
        hideTimer = 0;
      }
      // Restart the visibility clock each time we intentionally show the bar
      // (including after the skeleton overlay is removed).
      shownAt = Date.now();
      slide.classList.add("is-video-loading");
      updateLoadingProgress();
    };

    const hideLoading = (force = false) => {
      const finish = () => {
        slide.classList.remove("is-video-loading", "is-video-loading--indeterminate");
        if (loadingBar) loadingBar.style.width = "0";
        shownAt = 0;
        hideTimer = 0;
      };

      if (force) {
        if (hideTimer) window.clearTimeout(hideTimer);
        finish();
        return;
      }

      // Gallery is opacity:0 under the skeleton — don't dismiss yet or the bar
      // is gone by the time the user can see the video.
      if (galleryIsCovered()) return;

      const elapsed = Date.now() - (shownAt || Date.now());
      if (shownAt && elapsed < minVisibleMs) {
        if (hideTimer) window.clearTimeout(hideTimer);
        hideTimer = window.setTimeout(finish, minVisibleMs - elapsed);
        return;
      }
      finish();
    };

    // Keep the bar up while loading/buffering; only dismiss after real playback.
    video.addEventListener("loadstart", showLoading);
    video.addEventListener("waiting", showLoading);
    video.addEventListener("stalled", showLoading);
    video.addEventListener("seeking", showLoading);
    video.addEventListener("progress", () => {
      if (
        slide.classList.contains("is-video-loading") ||
        video.readyState < 3 ||
        video.seeking
      ) {
        showLoading();
      }
      updateLoadingProgress();
    });
    video.addEventListener("playing", () => {
      updateLoadingProgress();
      if (galleryIsCovered()) {
        showLoading();
        return;
      }
      const ratio = getBufferedRatio();
      if (ratio === null || ratio < 0.2 || video.readyState < 3) {
        showLoading();
        return;
      }
      hideLoading();
    });
    video.addEventListener("error", () => hideLoading(true));

    video._pdpVideoUI = {
      showLoading,
      hideLoading,
      updateLoadingProgress,
      syncLoadingState: () => {
        if (galleryIsCovered() || video.readyState < 3 || video.paused || video.seeking) {
          showLoading();
        } else {
          hideLoading();
        }
      },
    };
  };

  const activateVideo = (video) => {
    if (!(video instanceof HTMLVideoElement)) return;
    if (!video.isConnected) return;

    bindVideoLoadingBar(video);
    video._pdpVideoUI?.showLoading();

    if (video.dataset.pdpVideoActivated === "true") {
      video._pdpVideoUI?.updateLoadingProgress?.();
      return;
    }

    video.querySelectorAll("source[data-pdp-src]").forEach((source) => {
      source.src = source.dataset.pdpSrc;
      source.removeAttribute("data-pdp-src");
    });

    if (video.dataset.pdpPoster) {
      video.poster = video.dataset.pdpPoster;
      video.removeAttribute("data-pdp-poster");
    }

    video.dataset.pdpVideoActivated = "true";
    video.load();
  };

  const bindVideo = (video) => {
    if (!(video instanceof HTMLVideoElement) || boundVideos.has(video)) return;
    boundVideos.add(video);
    bindVideoLoadingBar(video);

    if (!("IntersectionObserver" in window)) {
      activateVideo(video);
      return;
    }

    if (!videoPreloadObserver) {
      videoPreloadObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            videoPreloadObserver.unobserve(entry.target);
            activateVideo(entry.target);
          });
        },
        { rootMargin: "300px 0px" },
      );
    }

    if (!videoPlaybackObserver) {
      videoPlaybackObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const currentVideo = entry.target;
            const galleryLoading = currentVideo
              .closest("gallery-section")
              ?.classList.contains("loading");

            if (galleryLoading) {
              // Warm sources under the skeleton, but keep the loading bar on so
              // it is still visible when the overlay is dismissed.
              activateVideo(currentVideo);
              currentVideo._pdpVideoUI?.showLoading?.();
              currentVideo.pause();
              return;
            }

            if (entry.isIntersecting && entry.intersectionRatio >= 0.45) {
              activateVideo(currentVideo);
              currentVideo._pdpVideoUI?.showLoading?.();
              currentVideo.muted = true;
              currentVideo.play().catch(() => undefined);
            } else {
              currentVideo.pause();
            }
          });
        },
        { threshold: [0, 0.45, 0.6] },
      );
    }

    videoPreloadObserver.observe(video);
    videoPlaybackObserver.observe(video);
  };

  window.refreshPdpGalleryVideos = () => {
    const videos = Array.from(
      document.querySelectorAll("[data-gallery] video"),
    );
    videos.forEach((video) => bindVideo(video));

    const isInView = (el) => {
      const ratio = el.getBoundingClientRect();
      return (
        ratio.width > 0 &&
        ratio.height > 0 &&
        ratio.bottom > 0 &&
        ratio.top < (window.innerHeight || 0) &&
        ratio.right > 0 &&
        ratio.left < (window.innerWidth || 0)
      );
    };

    // Activate only videos that are currently visible. Nearby videos are
    // already warmed by videoPreloadObserver's 300px root margin.
    videos.forEach((video) => {
      if (!isInView(video)) return;
      activateVideo(video);
      video._pdpVideoUI?.showLoading?.();
      if (!video.paused && video.readyState >= 3) {
        video._pdpVideoUI?.hideLoading?.();
        return;
      }
      video.muted = true;
      video.play().catch(() => undefined);
    });
  };

  const bindImage = (image) => {
    if (!(image instanceof HTMLImageElement)) return;
    if (boundImages.has(image)) {
      if (isLoaded(image)) markSkeleton(image, false);
      return;
    }
    boundImages.add(image);
    image.removeAttribute("data-pdp-skeleton-bound");

    const done = () => markSkeleton(image, false);

    if (isLoaded(image)) {
      done();
      return;
    }

    markSkeleton(image, true);
    image.addEventListener("load", done, { once: true });
    image.addEventListener("error", done, { once: true });

    // If a lazy image later becomes available without a new load event in some
    // edge cases, clear the skeleton on the next frame after decode.
    image.decode?.().then(done).catch(() => undefined);
    observeImage(image);
  };

  const start = () => {
    document
      .querySelectorAll("[data-gallery] .wt-product__img, [data-thumbs] .wt-product__img")
      .forEach(bindImage);
    document.querySelectorAll("[data-gallery] video").forEach(bindVideo);
  };

  window.bindPdpMediaSkeleton = bindImage;
  window.bindPdpMediaVideo = bindVideo;
  window.activatePdpVideo = activateVideo;

  document.addEventListener(
    "pointerdown",
    (event) => {
      const button = event.target.closest("model-element .model-btn");
      if (!button) return;
      button
        .closest("model-element")
        ?.querySelectorAll("iframe[data-pdp-src]")
        .forEach((iframe) => {
          iframe.src = iframe.dataset.pdpSrc;
          iframe.removeAttribute("data-pdp-src");
        });
    },
    { capture: true, passive: true },
  );

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start, { once: true });
  } else {
    start();
  }
})();
